import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Paper,
  Divider,
  Grid,
  LinearProgress,
  useTheme,
  TextField,
  Chip,
  Stack,
  Autocomplete,
  Collapse,
  IconButton,
  Badge,
  Avatar
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase-config";
import dayjs from "dayjs";
import AttachMoneyIcon from "@mui/icons-material/CurrencyPound";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { alpha } from "@mui/material/styles";
import appTheme from "../theme";

const SalesCards = () => {
  const theme = useTheme();
  
  // **ENHANCED STATE WITH MULTI-RESTAURANT FILTERING**
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedRestaurants, setSelectedRestaurants] = useState([]); // **CHANGED: Multiple restaurants**
  const [showFilters, setShowFilters] = useState(false); // **NEW: Toggle filters visibility**
  const [monthlyStats, setMonthlyStats] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
  });
  const [dailyStats, setDailyStats] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
  });
  const [invoices, setInvoices] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);

  // **FETCH INVOICES AND EXTRACT RESTAURANT LIST**
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        console.log("🔍 Fetching invoices for SalesCards...");
        const invoicesRef = collection(db, "invoices");
        const snapshot = await getDocs(invoicesRef);
        const fetchedInvoices = snapshot.docs.map((doc) => {
          const data = doc.data();
          console.log("📄 Invoice:", {
            id: doc.id,
            restaurantName: data.restaurantName,
            createdAt: data.createdAt,
            isBillPaid: data.isBillPaid,
            itemsCount: data.items?.length || 0
          });
          return data;
        });

        // **EXTRACT UNIQUE RESTAURANTS**
        const uniqueRestaurants = [...new Set(
          fetchedInvoices
            .map(invoice => invoice.restaurantName)
            .filter(name => name && name.trim() !== "")
        )].sort();

        console.log("🏪 Unique restaurants found:", uniqueRestaurants);

        setInvoices(fetchedInvoices);
        setRestaurants(uniqueRestaurants);
      } catch (error) {
        console.error("❌ Error fetching invoices:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  // **ENHANCED MONTHLY PROCESSING WITH MULTI-RESTAURANT FILTER**
  useEffect(() => {
    console.log("\n📊 Processing monthly data...");
    console.log("📊 Filters:", {
      selectedMonth,
      selectedRestaurants,
      totalInvoices: invoices.length
    });

    let monthData = invoices.filter((invoice) => {
      const invoiceDate = dayjs(invoice.createdAt);
      const monthMatches = invoiceDate.format("YYYY-MM") === selectedMonth;
      const restaurantMatches = selectedRestaurants.length === 0 || 
                               selectedRestaurants.includes(invoice.restaurantName);
      
      console.log(`📊 Invoice ${invoice.id || 'unknown'}:`, {
        date: invoiceDate.format("YYYY-MM"),
        restaurant: invoice.restaurantName,
        monthMatches,
        restaurantMatches,
        included: monthMatches && restaurantMatches
      });

      return monthMatches && restaurantMatches;
    });

    console.log(`📊 Filtered monthly invoices: ${monthData.length}`);

    // Calculate totals
    const total = monthData.reduce((sum, invoice) => {
      const invoiceTotal = invoice.items.reduce(
        (itemSum, item) => itemSum + (item.price * item.quantity || 0),
        0
      );
      return sum + invoiceTotal;
    }, 0);

    const paid = monthData
      .filter((invoice) => invoice.isBillPaid)
      .reduce((sum, invoice) => {
        const invoiceTotal = invoice.items.reduce(
          (itemSum, item) => itemSum + (item.price * item.quantity || 0),
          0
        );
        return sum + invoiceTotal;
      }, 0);

    const unpaid = total - paid;

    console.log("📊 Monthly Stats:", { total, paid, unpaid });
    setMonthlyStats({ total, paid, unpaid });
  }, [selectedMonth, selectedRestaurants, invoices]);

  // **ENHANCED DAILY PROCESSING WITH MULTI-RESTAURANT FILTER**
  useEffect(() => {
    console.log("\n📅 Processing daily data...");
    const dayString = selectedDate.format("YYYY-MM-DD");
    console.log("📅 Filters:", {
      selectedDate: dayString,
      selectedRestaurants,
      totalInvoices: invoices.length
    });

    let dayData = invoices.filter((invoice) => {
      const invoiceDate = dayjs(invoice.createdAt);
      const dayMatches = invoiceDate.format("YYYY-MM-DD") === dayString;
      const restaurantMatches = selectedRestaurants.length === 0 || 
                               selectedRestaurants.includes(invoice.restaurantName);

      console.log(`📅 Invoice ${invoice.id || 'unknown'}:`, {
        date: invoiceDate.format("YYYY-MM-DD"),
        restaurant: invoice.restaurantName,
        dayMatches,
        restaurantMatches,
        included: dayMatches && restaurantMatches
      });

      return dayMatches && restaurantMatches;
    });

    console.log(`📅 Filtered daily invoices: ${dayData.length}`);

    // Calculate totals
    const total = dayData.reduce((sum, invoice) => {
      const invoiceTotal = invoice.items.reduce(
        (itemSum, item) => itemSum + (item.price * item.quantity || 0),
        0
      );
      return sum + invoiceTotal;
    }, 0);

    const paid = dayData
      .filter((invoice) => invoice.isBillPaid)
      .reduce((sum, invoice) => {
        const invoiceTotal = invoice.items.reduce(
          (itemSum, item) => itemSum + (item.price * item.quantity || 0),
          0
        );
        return sum + invoiceTotal;
      }, 0);

    const unpaid = total - paid;

    console.log("📅 Daily Stats:", { total, paid, unpaid });
    setDailyStats({ total, paid, unpaid });
  }, [selectedDate, selectedRestaurants, invoices]);

  // **UTILITY FUNCTIONS**
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getPaymentPercentage = (paid, total) => {
    if (total === 0) return 0;
    return (paid / total) * 100;
  };

  const generateMonthOptions = () => {
    const options = [];
    const startDate = dayjs("2024-12-01");
    const currentDate = dayjs();

    let month = startDate;
    while (month.isBefore(currentDate) || month.isSame(currentDate, "month")) {
      const monthValue = month.format("YYYY-MM");
      options.push(
        <MenuItem key={monthValue} value={monthValue}>
          {month.format("MMMM YYYY")}
        </MenuItem>
      );
      month = month.add(1, "month");
    }

    return options;
  };

  // **CLEAR ALL FILTERS**
  const handleClearAllFilters = () => {
    console.log("🔄 Clearing all restaurant filters");
    setSelectedRestaurants([]);
  };

  // **REMOVE SPECIFIC RESTAURANT**
  const handleRemoveRestaurant = (restaurantToRemove) => {
    console.log("🔄 Removing restaurant filter:", restaurantToRemove);
    setSelectedRestaurants(prev => prev.filter(r => r !== restaurantToRemove));
  };

  // **TOGGLE FILTERS VISIBILITY**
  const handleToggleFilters = () => {
    setShowFilters(prev => !prev);
  };

  // **CHECK IF ANY FILTERS ARE ACTIVE**
  const hasActiveFilters = selectedRestaurants.length > 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2 }}>
        {loading && <LinearProgress />}

        {/* **UNIFIED CONTAINER WITH INTEGRATED FILTERS** */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            mb: 3,
            transition: "transform 0.2s, box-shadow 0.2s",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 6,
            },
          }}
        >
          {/* **HEADER WITH FILTER TOGGLE** */}
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ 
                    bgcolor: appTheme.colors.champagne,
                    color: appTheme.colors.primary,
                    width: 32,
                    height: 32
                  }}>
                    📊
                  </Avatar>
                  <Typography variant="h6" component="span">
                    Sales Analytics Dashboard
                  </Typography>
                  {hasActiveFilters && (
                    <Badge 
                      badgeContent={selectedRestaurants.length} 
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: appTheme.colors.champagne,
                          color: appTheme.colors.primary,
                          fontWeight: 'bold'
                        }
                      }}
                    >
                      <FilterListIcon sx={{ color: appTheme.colors.champagne }} />
                    </Badge>
                  )}
                </Box>
                
                <IconButton 
                  onClick={handleToggleFilters}
                  sx={{ 
                    color: appTheme.colors.champagne,
                    '&:hover': {
                      backgroundColor: alpha(appTheme.colors.champagne, 0.1)
                    }
                  }}
                >
                  {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            }
            sx={{
              bgcolor: appTheme.colors.secondary,
              color: theme.palette.primary.contrastText,
              pb: 1,
            }}
          />

          {/* **COLLAPSIBLE FILTER SECTION** */}
          <Collapse in={showFilters}>
            <Box sx={{ 
              p: 3, 
              backgroundColor: alpha(appTheme.colors.surface, 0.7),
              borderBottom: `1px solid ${appTheme.colors.borderSubtle}`
            }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Autocomplete
                    multiple
                    options={restaurants}
                    value={selectedRestaurants}
                    onChange={(event, newValue) => {
                      console.log("🏪 Restaurant filters changed:", newValue);
                      setSelectedRestaurants(newValue);
                    }}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          icon={<RestaurantIcon />}
                          sx={{
                            backgroundColor: appTheme.colors.primary,
                            color: appTheme.colors.surface,
                            '& .MuiChip-deleteIcon': {
                              color: appTheme.colors.surface,
                              '&:hover': {
                                color: appTheme.colors.champagne
                              }
                            }
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filter by Restaurants (Multi-select)"
                        placeholder="Select restaurants..."
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <RestaurantIcon sx={{ mr: 1, color: appTheme.colors.secondary }} />
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <RestaurantIcon sx={{ mr: 1, color: appTheme.colors.secondary }} />
                        {option}
                      </Box>
                    )}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                {hasActiveFilters && (
                  <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      <Typography variant="body2" color="textSecondary">
                        {selectedRestaurants.length} restaurant{selectedRestaurants.length !== 1 ? 's' : ''} selected
                      </Typography>
                      <IconButton 
                        onClick={handleClearAllFilters}
                        size="small"
                        sx={{ 
                          color: appTheme.colors.error,
                          '&:hover': {
                            backgroundColor: alpha(appTheme.colors.error, 0.1)
                          }
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Stack>
                  </Grid>
                )}
              </Grid>

              {/* **ACTIVE FILTERS DISPLAY** */}
              {hasActiveFilters && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Active Filters:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectedRestaurants.map((restaurant) => (
                      <Chip
                        key={restaurant}
                        icon={<RestaurantIcon />}
                        label={restaurant}
                        onDelete={() => handleRemoveRestaurant(restaurant)}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: 2,
                          '& .MuiChip-deleteIcon': {
                            color: appTheme.colors.primary
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Collapse>

          {/* **SALES CARDS IN SAME CONTAINER** */}
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* **MONTHLY SALES TRENDS** */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(appTheme.colors.primary, 0.05),
                    border: `1px solid ${alpha(appTheme.colors.primary, 0.2)}`,
                    height: "100%",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(appTheme.colors.primary, 0.08),
                      border: `1px solid ${alpha(appTheme.colors.primary, 0.3)}`,
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarTodayIcon sx={{ color: appTheme.colors.primary }} />
                    <Typography variant="h6" sx={{ color: appTheme.colors.primary, fontWeight: 'bold' }}>
                      Monthly Sales Trends
                    </Typography>
                    {hasActiveFilters && (
                      <Chip 
                        label={`${selectedRestaurants.length} selected`}
                        size="small" 
                        sx={{ 
                          backgroundColor: appTheme.colors.secondary,
                          color: appTheme.colors.surface,
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>

                  <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => {
                        console.log("📅 Month changed:", e.target.value);
                        setSelectedMonth(e.target.value);
                      }}
                      label="Month"
                    >
                      {generateMonthOptions()}
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="primary"
                      sx={{ mb: 2 }}
                    >
                      Total Revenue: {formatCurrency(monthlyStats.total)}
                    </Typography>

                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">Paid</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(monthlyStats.paid)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getPaymentPercentage(
                          monthlyStats.paid,
                          monthlyStats.total
                        )}
                        color="success"
                        sx={{ height: 8, borderRadius: 5, mb: 1.5 }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">Unpaid</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(monthlyStats.unpaid)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getPaymentPercentage(
                          monthlyStats.unpaid,
                          monthlyStats.total
                        )}
                        color="warning"
                        sx={{ height: 8, borderRadius: 5 }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* **DAILY SALES TRENDS** */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(appTheme.colors.secondary, 0.05),
                    border: `1px solid ${alpha(appTheme.colors.secondary, 0.2)}`,
                    height: "100%",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(appTheme.colors.secondary, 0.08),
                      border: `1px solid ${alpha(appTheme.colors.secondary, 0.3)}`,
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoneyIcon sx={{ color: appTheme.colors.secondary }} />
                    <Typography variant="h6" sx={{ color: appTheme.colors.secondary, fontWeight: 'bold' }}>
                      Daily Sales Trends
                    </Typography>
                    {hasActiveFilters && (
                      <Chip 
                        label={`${selectedRestaurants.length} selected`}
                        size="small" 
                        sx={{ 
                          backgroundColor: appTheme.colors.primary,
                          color: appTheme.colors.surface,
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>

                  <TextField
                    label="Select Date"
                    type="date"
                    value={selectedDate.format('YYYY-MM-DD')}
                    onChange={(e) => {
                      console.log("📅 Date changed:", e.target.value);
                      setSelectedDate(dayjs(e.target.value));
                    }}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ mb: 3 }}
                  />

                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="primary"
                      sx={{ mb: 2 }}
                    >
                      Total Revenue: {formatCurrency(dailyStats.total)}
                    </Typography>

                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">Paid</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(dailyStats.paid)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getPaymentPercentage(
                          dailyStats.paid,
                          dailyStats.total
                        )}
                        color="success"
                        sx={{ height: 8, borderRadius: 5, mb: 1.5 }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">Unpaid</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(dailyStats.unpaid)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getPaymentPercentage(
                          dailyStats.unpaid,
                          dailyStats.total
                        )}
                        color="warning"
                        sx={{ height: 8, borderRadius: 5 }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Paper>

        {/* **FILTER SUMMARY (Only when filters are active and hidden)** */}
        {hasActiveFilters && !showFilters && (
          <Paper
            elevation={1}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha(appTheme.colors.primary, 0.05),
              border: `1px solid ${alpha(appTheme.colors.primary, 0.2)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                📊 Showing data for {selectedRestaurants.length} selected restaurant{selectedRestaurants.length !== 1 ? 's' : ''}:
                <strong> {selectedRestaurants.join(', ')}</strong>
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => setShowFilters(true)}
                sx={{ color: appTheme.colors.primary }}
              >
                <FilterListIcon />
              </IconButton>
            </Box>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default SalesCards;
